/**
 * The address we last emailed a sign-in link to.
 *
 * ⚠⚠ Why this exists: the dead-link notice used to end with "ask for a fresh one
 * below", and below it sat the PASSWORD form. To obey the sentence the person
 * had to spot a small teal "Email me a sign-in link instead", tap it, and type
 * their address again from memory, on a phone, having just been told something
 * went wrong. That is not a fix, it is a scavenger hunt, and a client gave up on
 * it on 3 Sep 2026.
 *
 * Remembering the address turns the notice into one button that actually sends.
 *
 * `localStorage`, not `session` — the emailed link very often opens in a NEW tab
 * or even a different browser, and a sessionStorage slot is empty there, which is
 * exactly the tab that needs to know. It holds one thing the person typed about
 * themselves, on their own device, next to the session token the auth client
 * already keeps in the same store.
 *
 * @since 2026-09-03
 */

const KEY = 'karr_auth_last_email';

/** Called wherever we ask GoTrue to send a link, so every send site is covered. */
export function rememberAuthEmail(email: string): void {
    try {
        window.localStorage.setItem(KEY, email);
    } catch { /* private mode — the notice falls back to asking for the address */ }
}

/**
 * The remembered address, or null.
 *
 * Re-validated on the way out rather than trusted: this is a string a previous
 * build (or a person with devtools) wrote, and it is about to be put in a
 * sentence and posted to GoTrue.
 */
export function recallAuthEmail(): string | null {
    try {
        const v = window.localStorage.getItem(KEY);
        return v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : null;
    } catch {
        return null;
    }
}

/** Forget it — on sign-out, so a shared machine does not name the last person. */
export function forgetAuthEmail(): void {
    try {
        window.localStorage.removeItem(KEY);
    } catch { /* ignore */ }
}
